import {
  HourSelectorType,
  PageCategory,
  PageConfigWithCharts,
} from '../../../shared/features/ng-redux/page/page.models';
import { ApplicationConfigWithPages } from '../../../shared/features/ng-redux/app/appConfig.models';

import {
  BarChartConfig,
  ChartTypeEnum,
  DonutChartConfig,
  LineChartConfig,
  MostLeastEnum,
  TableConfig,
  TimelineChartConfig,
} from '../../../shared/models/chart.models';

import {
  DEVICE_BATTERY_USAGE_W,
  DEVICE_LAST_KNOWN,
  DEVICE_SIGNAL_STRENGTH,
  SMART_BATTERY_SPOTLIGHT,
  DEVICE_APP_USAGE,
  CHART_UI_BAR_TOPN,
  DEFAULT_DEVICE_FILTER_BAR_CONFIG,
  DEVICE_LIST_W,
  DEVICE_DATA_USAGE_W,
  CHART_UI_DONUT_LEAST_MOST,
  DEVICE_WIFI,
  DEVICE_SIGNAL_STRENGTH_W,
  CHART_UI_BAR_TOPN_MANY,
} from './app-mapper.constants';

import { buildChartOptions, findPageId, patchCard, patchChart, patchPage } from './app-mapper.utils';
import { ColorConstants, HighchartsConstant, QUALITY_COLORS } from '../../../../../../xs-colors';
import { InsSelectionType, TableRenderEnum } from '../../../../../../../libs/table/src';
import { CONSTANTS } from '../../../shared/localizations/dictionary';
import { ProfilePermissionEnum } from '../../../../../../../libs/services';

function patchWindowsDeviceListPage(page: PageConfigWithCharts) {
  page.filterBarVisible = true;
  page.filterBarConfig = DEFAULT_DEVICE_FILTER_BAR_CONFIG;
  page.pageCategory = PageCategory.DEVICE;
  page.claims = [
    ProfilePermissionEnum.ProfileViewBatteryStatusDashboard,
    ProfilePermissionEnum.ProfileViewWifiDashboard,
    ProfilePermissionEnum.ProfileViewDataUsageDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    ProfilePermissionEnum.ProfileViewLastLocationDashboard,
    ProfilePermissionEnum.ProfileViewSignalStrengthDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
    ProfilePermissionEnum.ProfileViewWebAppUsageDashboard,
  ];
  patchChart(page.charts, 'device-list-table_w', (chart: TableConfig) => {
    chart.name = 'Ticket_List_Devices_Header';

    chart.selectionType = InsSelectionType.multiple;
    chart.selectionText = 'device_selected_label';
    chart.selectionTextPlural = 'device_selected_label';
    chart.rowActions = [
      {
        action: 'customAttributes',
        tooltip: 'custom_attr_title',
        icon: 'insight-icon-deviceinfo',
      },
    ];

    chart.filters = {
      sortAttribute: 'McDeviceId',
      sortDirection: 'desc',
      pageSize: 50,
      pageSkip: 0,
    };
    chart.tableAttributes = [
      {
        id: 'DeviceName',
        label: 'Column_Name_Device_Name',
        renderer: TableRenderEnum.LINK,
        primaryColumnSortId: 'DeviceName',
        rendererParams: {
          eventId: 'deviceClicked',
          icons: [
            {
              iconClass: 'insight-icon-type-DeviceWindowsDesktop',
            },
          ],
        },
      },
      {
        id: 'McDeviceId',
        label: 'Column_Name_Device_Id',
        renderer: TableRenderEnum.STRING,
      },
      {
        id: 'Name',
        label: 'Column_Name_Device_Group',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'GroupPath',
        label: 'group_path_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'ManufacturerName',
        label: 'label_Manufacturer',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'ModelName',
        label: 'Column_Name_Model',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'OSVersion',
        label: 'Column_Name_Os_Version',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        primaryColumnSortId: 'OSVersionAsPaddedString',
      },
      {
        id: 'storageFreeLastKnown',
        label: 'availablestorage_label',
        renderer: TableRenderEnum.PROGRESS_BAR,
        iconClass: '',
        primaryColumnSortId: 'storageFreeLastKnown',
      },
      {
        id: 'MACAddress',
        label: 'label_MACAddress',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
        shouldTranslate: true,
      },
    ];
  });
}

function patchDeviceWindowsCellular(page: PageConfigWithCharts) {
  page.claims = [ProfilePermissionEnum.ProfileViewSignalStrengthDashboard];
  page.multiHourSelector = true;
  page.hourEnabled = true;
  page.hourSelectorType = HourSelectorType.CHECKBOX;

  if (page.charts) {
    page.predefinedCharts = [
      {
        category: {
          name: 'Siganl_Strength_Charts_Category_Header_Summary',
          icon: 'insight-icon-battery-usage',
          tooltip: 'Signal_Strength_Charts_Category_header_Summary_Information_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'device-ss-heatmap_w', 'Device_Charts_Category_Chart_Caption_heatmap'),
          buildChartOptions(page.charts, 'device-ss-ss_w', 'Device_Charts_Category_Chart_Caption_SS_SS'),
          buildChartOptions(page.charts, 'device-ss-conn_w', 'Device_Charts_Category_Chart_Caption_SS_Connection'),
          buildChartOptions(page.charts, 'device-ss-ntt_w', 'Device_Charts_Category_Chart_Caption_SS_NTT'),
          buildChartOptions(page.charts, 'device-ss-do_w', 'Device_Charts_Category_Chart_Caption_SS_Total_DO'),
          buildChartOptions(page.charts, 'device-ss-ss-ts_w', 'Device_Charts_Category_Chart_Caption_SS_AVG_SS'),
        ],
      },
    ];

    patchCard(page.charts, 'device-ss-heatmap_w', card => {
      card.icon = 'insight-icon-signal-strength';
      card.iconTooltip = 'sdd_signal_strength_icon_tooltip';
      card.name = 'sdd_signal_strength_name';
      card.type = 'ss-map';
      card.noDataIcon = 'insight-icon-nolocation';
      card.cardStyle = ['static', 'card-2xFH'];
    });

    patchCard(page.charts, 'device-ss-ntt_w', (chart: LineChartConfig) => {
      chart.iconTooltip = 'ss_network_type_usage_fullname';
      chart.name = 'ss_network_type_usage_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'ss_network_type_usage_metric_label',
      };
      chart.metrics = [
        {
          id: 'nt_1',
        },
        {
          id: 'nt_2',
        },
        {
          id: 'nt_3',
        },
        {
          id: 'nt_4',
        },
        {
          id: 'nt_5',
        },
        {
          id: 'nt_6',
        },
      ];

      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'ss_network_type_usage_tooltip_value',
      };

      chart.legends = [
        'ss_network_type_usage_metric_label_lte',
        'ss_network_type_usage_metric_label_2g',
        'ss_network_type_usage_metric_label_3g',
        'ss_network_type_usage_metric_label_4g',
        'ss_network_type_usage_metric_label_5g',
        'ss_network_type_usage_metric_label_iwlan',
      ];
      chart.formatDate = true;
      chart.integerOnly = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = false;
      chart.hourSwitch = true;
    });

    patchCard(page.charts, 'device-ss-do_w', (chart: LineChartConfig) => {
      chart.iconTooltip = 'ss_total_signal_dropout_fullname';
      chart.name = 'ss_total_signal_dropout_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'do',
        label: 'ss_total_signal_dropout_metric_label',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_ss_total_signal_drop_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.dateFormatStr = 'LL';
      chart.formatDate = true;
      chart.hourSwitch = true;
    });

    patchCard(page.charts, 'device-ss-ss-ts_w', (chart: LineChartConfig) => {
      chart.iconTooltip = 'ss_avg_signal_strength_fullname';
      chart.name = 'ss_avg_signal_strength_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'ss',
        label: 'ss_avg_signal_strength_metric_label',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_ss_avg_signal_strength_tooltip_value',
      };
      chart.formatDate = true;
      chart.legendDisabled = true;
      chart.dateFormatStr = 'LL';
      chart.allowNegativeValue = true;
      chart.hourSwitch = true;
    });

    patchCard<BarChartConfig>(page.charts, 'device-ss-conn_w', chart => {
      chart.iconTooltip = 'sdd_ss_chart_connection_tooltip';
      chart.name = 'sdd_ss_chart_connection_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.dimension = {
        dimensionId: 'name',
        label: 'sdd_ss_chart_connection_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'readings_label',
      };
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('sdd_ss_chart_connection_tooltip_label',  xsI18nPipe.transform(this.point.name))}`",
        labelArgs: ['sdd_ss_chart_connection_tooltip_label', 'xsI18nPipe.transform(this.point.name)'],
        value:
          "`${xsI18nPipe.transform('sdd_ss_chart_connection_tooltip_value', this.point.y, (this.point.y/this.point.totalReadings*100).toFixed(2).replace(/\\.?0+$/,''), this.point.totalReadings)}`",
        valueArgs: [
          'sdd_ss_chart_connection_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.totalReadings)',
          'this.point.totalReadings',
        ],
      };
    });

    patchCard<DonutChartConfig>(page.charts, 'device-ss-ss_w', chart => {
      chart.iconTooltip = 'sdd-ss-signalStrength-iconTooltip';
      chart.type = ChartTypeEnum.DONUT;
      chart.name = 'sdd_data_usage_signal_name';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_ss_chart_ss_tooltip_label", xsI18nPipe.transform(this.point.name))}`',
        labelArgs: ['sdd_ss_chart_ss_tooltip_label', 'xsI18nPipe.transform(this.point.name)'],
        value:
          "`${xsI18nPipe.transform('sdd_ss_chart_ss_tooltip_value', this.point.y, (this.point.y/this.point.totalReadings*100).toFixed(2).replace(/\\.?0+$/,''), this.point.totalReadings)}`",
        valueArgs: [
          'sdd_ss_chart_ss_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.totalReadings)',
          'this.point.totalReadings',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'sdd_ss_chart_ss_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
    });
  }

  page.icon = 'insight-icon-signal-strength';
  page.name = 'sdd_signal_strength_name';
}

function patchDeviceWindowsBatteryPage(page: PageConfigWithCharts) {
  // add icon, iconTooltip, noDataIcon, cardstyle, chartheight, timelineColor
  page.claims = [ProfilePermissionEnum.ProfileViewBatteryStatusDashboard];
  if (page.charts) {
    patchCard(page.charts, 'device-battery-overviewCard_w', card => {
      card.name = 'app_mapper_sdd_Overview';
    });

    patchCard<BarChartConfig>(page.charts, 'device-battery-appDrainCard_w', chart => {
      chart.iconTooltip = 'sdd_battery_chart_app_drain_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_battery_chart_app_drain_name';

      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_battery_chart_app_drain_dimension_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'percentTotalAppDrain',
        label: 'sdd_battery_chart_app_drain_metric_label',
        format: '%(value).2f%%',
      };
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('xs_app_name_label', this.point.label, this.point.packageId)}`",
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value: "`${xsI18nPipe.transform('sdd_battery_chart_app_drain_tooltip_value', this.point.y)}`",
        valueArgs: ['sdd_battery_chart_app_drain_tooltip_value', 'this.point.y'],
      };

      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<DonutChartConfig>(page.charts, 'device-battery-chargePatternCard_w', chart => {
      chart.iconTooltip = 'app-mapper-sdd-device-battery-chargePatternCard-iconTooltip';
      chart.chartHeight = 240;

      chart.name = 'sdd_battery_charge_charge_pattern_name';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_battery_charge_charge_pattern_tooltip_label", this.point.name)}`',
        labelArgs: ['sdd_battery_charge_charge_pattern_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_battery_charge_charge_pattern_tooltip_value", this.point.y, (this.point.ratio * 100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.totalCount)}`',
        valueArgs: [
          'sdd_battery_charge_charge_pattern_tooltip_value',
          'this.point.y',
          'this.point.ratio',
          'this.point.totalCount',
        ],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'sdd_battery_chart_charge_pattern_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'count',
        label: 'sdd_battery_chart_charge_pattern_metric_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'chargePatternBucket',
      };
      chart.fixedColors = [
        {
          label: 'sdd_battery_chart_charge_pattern_spot_charged_color_label',
          color: HighchartsConstant.defaultBucket.average,
        },
        {
          label: 'sdd_battery_chart_charge_pattern_randomly_charged_color_label',
          color: QUALITY_COLORS.lime,
        },
        {
          label: 'sdd_battery_chart_charge_pattern_overcharged_color_label',
          color: HighchartsConstant.defaultBucket.bad,
        },
        {
          label: 'sdd_battery_chart_charge_pattern_optimally_charged_color_label',
          color: HighchartsConstant.defaultBucket.good,
        },
      ];
    });

    patchCard<BarChartConfig>(page.charts, 'device-battery-chargeLevelCard_w', chart => {
      chart.iconTooltip = 'app-mapper-sdd-device-battery-chargeLevelCard-iconTooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_battery_chart_charge_level_hour_name';

      chart.integerOnly = true;
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.fixedColors = [
        {
          color: ColorConstants.sotiColors('gradient', 5),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-0-9Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 10),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-10-19Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 20),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-20-29Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 30),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-30-39Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 40),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-40-49Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 50),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-50-59Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 60),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-60-69Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 70),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-70-79Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 80),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-80-89Percent',
        },
        {
          color: ColorConstants.sotiColors('gradient', 90),
          label: 'app-mapper-sdd-device-battery-chargeLevelCard-chart-fixedColors-90-100Percent',
        },
      ];
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('sdd_battery_chart_battery_charge_level_hour_tooltip_label', xsI18nPipe.transform(this.point.hr), xsI18nPipe.transform(this.point.label))}`",
        labelArgs: [
          'sdd_battery_chart_battery_charge_level_hour_tooltip_label',
          'xsI18nPipe.transform(this.point.hr)',
          'xsI18nPipe.transform(this.point.label)',
        ],
        value:
          "`${xsI18nPipe.transform('sdd_battery_chart_battery_charge_level_hour_tooltip_value', this.point.y, (this.point.y/this.point.total*100).toFixed(2).replace(/\\.?0+$/,''), this.point.total)}`",
        valueArgs: [
          'sdd_battery_chart_battery_charge_level_hour_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.total)',
          'this.point.total',
        ],
      };
      chart.tag = {
        name: 'sdd_battery_chart_charge_level_hour_name',
        parameters: ['hour'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'sdd_battery_chart_charge_level_hour_dimension_label',
        noDataLabel: '{NO_DATA}',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'sdd_battery_chart_charge_level_hour_metric_label',
        format: '%(value).1f%%',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'batteryLevelAtHourBucket',
      };
      chart.filters = {
        hour: '8',
      };
      chart.uiSettings = [
        {
          id: 'hour',
          controlType: 'SELECTBOX',
          type: 'number',
          displayType: 'dropdown',
          options: [
            {
              value: 0,
              label: '0_hour',
            },
            {
              value: 1,
              label: '1_hour',
            },
            {
              value: 2,
              label: '2_hour',
            },
            {
              value: 3,
              label: '3_hour',
            },
            {
              value: 4,
              label: '4_hour',
            },
            {
              value: 5,
              label: '5_hour',
            },
            {
              value: 6,
              label: '6_hour',
            },
            {
              value: 7,
              label: '7_hour',
            },
            {
              value: 8,
              label: '8_hour',
            },
            {
              value: 9,
              label: '9_hour',
            },
            {
              value: 10,
              label: '10_hour',
            },
            {
              value: 11,
              label: '11_hour',
            },
            {
              value: 12,
              label: '12_hour',
            },
            {
              value: 13,
              label: '13_hour',
            },
            {
              value: 14,
              label: '14_hour',
            },
            {
              value: 15,
              label: '15_hour',
            },
            {
              value: 16,
              label: '16_hour',
            },
            {
              value: 17,
              label: '17_hour',
            },
            {
              value: 18,
              label: '18_hour',
            },
            {
              value: 19,
              label: '19_hour',
            },
            {
              value: 20,
              label: '20_hour',
            },
            {
              value: 21,
              label: '21_hour',
            },
            {
              value: 22,
              label: '22_hour',
            },
            {
              value: 23,
              label: '23_hour',
            },
          ],
        },
      ];
    });

    patchCard<TimelineChartConfig>(page.charts, 'device-battery-chargeLevetTS_w', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_battery_level_ts_tooltip';
      chart.chartHeight = 240;
      chart.name = 'sdd_battery_level_ts_name';

      chart.dimension = {
        dimensionId: 'CollectedTime',
        label: 'sdd_battery_level_ts_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'TotalUsage',
        label: 'sdd_battery_level_ts_metric_label',
      };
      chart.tooltip = {
        label: 'sdd_battery_level_ts_tooltip_label',
        value: 'sdd_battery_level_ts_tooltip_value',
      };
      chart.xMinRange = 1000 * 60 * 60;
      chart.legendDisabled = true;
    });
  }

  page.applicationType = ['battery'];
  page.icon = 'insight-icon-battery-usage';
  page.name = 'Battery_Status_Charts_Category_Header';

  page.predefinedCharts = [
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header_Summary',
        icon: 'insight-icon-battery-usage',
        tooltip: 'Battery_Status_Charts_Category_header_Summary_Information_Tooltip',
      },
      charts: [buildChartOptions(page.charts, 'device-battery-overviewCard_w', 'app_mapper_sdd_Overview')],
    },
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header',
        icon: 'insight-icon-battery-usage',
        tooltip: 'Battery_Status_Charts_Category_Header_Battery_Status_Tooltip',
      },
      charts: [
        buildChartOptions(
          page.charts,
          'device-battery-appDrainCard_w',
          'Device_Charts_Category_Chart_Caption_Top_App_Drain'
        ),
        buildChartOptions(page.charts, 'device-battery-chargeLevetTS_w', 'sdd_battery_level_ts_name'),
      ],
    },
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header_Charging',
        icon: 'insight-icon-battery-charging',
        tooltip: 'Battery_Status_Charts_Category_Header_Charging_Tooltip',
      },
      charts: [
        buildChartOptions(
          page.charts,
          'device-battery-chargePatternCard_w',
          'Battery_Status_Charts_Category_Charge_Pattern'
        ),
        buildChartOptions(
          page.charts,
          'device-battery-chargeLevelCard_w',
          'Device_Charts_Category_Chart_Caption_Charge_Level'
        ),
      ],
    },
  ];
}

function patchDeviceWindowsDataUsagePage(page: PageConfigWithCharts) {
  page.claims = [ProfilePermissionEnum.ProfileViewDataUsageDashboard];
  if (page.charts) {
    patchChart(page.charts, 'ddTotalUsageTS_w', (chart: LineChartConfig) => {
      chart.fullName = 'sdd_data_chart_data_usage_time_fullname';
      chart.name = 'sdd_data_chart_data_usage_time_name';
      chart.iconTooltip = 'sdd_data_chart_data_usage_time_icon_tooltip';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_chart_usage_time__metric_label',
      };
      chart.tag = {
        name: 'sdd_data_chart_data_usage_time_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_chart_data_usage_time_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });

    patchCard<BarChartConfig>(page.charts, 'device-data-appUsageCard_w', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_app_usage_icon_tooltip';
      chart.name = 'sdd_data_usage_app_usage_name';
      chart.layout = '50p';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label:
          '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("sdd_data_usage_app_usage_tooltip_value", this.point.ytip, (this.point.y / this.point.total * 100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.totalTip)}`',
        valueArgs: [
          'sdd_data_usage_app_usage_tooltip_value',
          'this.point.ytip',
          'percentage(this.point.y, this.point.total)',
          'this.point.totalTip',
        ],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_dimension_application_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_app_usage_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'appUsage',
        label: 'data_usage_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageId',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<BarChartConfig>(page.charts, 'device-data-browserUsageCard_w', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_browser_icon_tooltip';
      chart.layout = '50p';
      chart.name = 'sdd_data_usage_browser_name';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label:
          '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("sdd_data_usage_browser_tooltip_value", this.point.ytip, (this.point.y / this.point.total * 100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.totalTip)}`',
        valueArgs: [
          'sdd_data_usage_browser_tooltip_value',
          'this.point.ytip',
          'percentage(this.point.y, this.point.total)',
          'this.point.totalTip',
        ],
      };
      chart.dimension = {
        dimensionId: 'appName',
        label: 'sdd_data_usage_browser_label',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'appUsage',
        label: 'data_usage_label',
      };
      chart.filter = {
        type: 'mapped',
        attribute: 'packageId',
      };
      chart.tag = {
        name: 'sdd_data_usage_app_usage_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard<TimelineChartConfig>(page.charts, 'device-data-totalUsageCard_w', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_by_connection_type_icon_tooltip';
      chart.name = 'sdd_data_usage_by_connection_type_name';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'data_chart_time_dimension_label',
        noDataLabel: 'no_data_label',
      };

      chart.timelineColors = [
        {
          label: 'sdd_data_usage_total_wifi_usage',
          color: ColorConstants.sotiColors('yellow', 90),
        },
        {
          label: 'sdd_data_usage_total_ether_usage',
          color: ColorConstants.sotiColors('cerise', 5),
        },
        {
          label: 'sdd_data_usage_total_cellular_usage',
          color: ColorConstants.sotiColors('blue', 70),
        },
        {
          label: 'sdd_data_usage_total_roaming_usage',
          color: ColorConstants.sotiColors('green', 60),
        },
        {
          label: 'sdd_data_usage_total_total_usage',
          color: ColorConstants.sotiColors('purple', 70),
        },
      ];
      chart.multiTimelineMetric = [
        {
          metricId: 'allY',
          label: 'sdd_data_usage_by_connection_type',
        },
        { metricId: 'wifiY', label: 'sdd_data_usage_by_connection_type_wifi_usage' },
        { metricId: 'etherY', label: 'sdd_data_usage_by_connection_type_ether_usage' },
        { metricId: 'cellY', label: 'sdd_data_usage_by_connection_type_cellular_usage' },
        { metricId: 'roamY', label: 'sdd_data_usage_by_connection_type_roam_usage' },
      ];
      chart.metric = {
        metricId: '',
        label: 'data_usage_label',
      };
      chart.tooltip = {
        label: 'sdd_data_usage_by_connection_type_tooltip_label',
        value: 'sdd_data_usage_by_connection_type_tooltip_value',
        dateTime: 'sdd_data_usage_by_connection_type_tooltip_date_time',
      };
    });

    patchCard<DonutChartConfig>(page.charts, 'device-data-uploadDownloadCard_w', chart => {
      chart.icon = 'insight-icon-moreaction';
      chart.iconTooltip = 'sdd_data_usage_upload_download_icon_tooltip';
      chart.name = 'sdd_data_usage_upload_download_name';
      chart.layout = '50p';
      chart.isFilterable = false;
      chart.allowPointSelect = false;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("sdd_data_usage_upload_download_tooltip_label", this.point.name)}`',
        labelArgs: ['sdd_data_usage_upload_download_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("sdd_data_usage_upload_download_tooltip_value", this.point.yStr, (this.total ? (" (" + (this.point.y / this.total * 100).toFixed(2).replace(/\\.?0+$/,\'\') + "%)") : ""), this.point.totalStr)}`',
        valueArgs: [
          'sdd_data_usage_upload_download_tooltip_value',
          'this.point.yStr',
          'percentage(this.point.y, this.point.total)',
          'this.point.totalStr',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'sdd_data_usage_upload_download_name',
        noDataLabel: 'sdd_no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_upload_download_data_use_label',
      };
      chart.fixedColors = [
        {
          label: '{DATA_UPLOAD}',
          color: HighchartsConstant.dataUsageStaticBucket.item1,
        },
        {
          label: '{DATA_DOWNLOAD}',
          color: HighchartsConstant.dataUsageStaticBucket.item2,
        },
      ];
    });

    patchChart(page.charts, 'ddAvgConnTimeTS_w', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_avg_conn_time_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_avg_conn_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'sdd_data_usage_avg_conn_time_ts_metric_label',
      };
      chart.metrics = [
        {
          id: 'timeCell',
          timeFormatValueId: 'hourMinute1',
          countId: 'devCell',
        },
        {
          id: 'timeWifi',
          timeFormatValueId: 'hourMinute2',
          countId: 'devWiFi',
        },
      ];
      chart.tag = {
        name: 'sdd_data_usage_avg_conn_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_avg_conn_time_ts_tooltip_value',
      };
      chart.legendDisabled = false;
      chart.legends = [
        'sdd_data_usage_avg_conn_time_ts_legend_cellular',
        'sdd_data_usage_avg_conn_time_ts_legend_wifi',
      ];
      chart.formatDate = true;
      chart.formatTime = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'ddRoamUsageTS_w', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_usage_roaming_usage_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_roaming_usage_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_roaming_usage_ts_metric_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_roaming_usage_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_roaming_usage_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'ddRoamTimeTS_w', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_usage_roaming_time_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_roaming_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_roaming_time_ts_metric_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_roaming_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_roaming_time_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'ddVpnTimeTS_w', (chart: LineChartConfig) => {
      chart.iconTooltip = 'sdd_data_usage_vpn_time_ts_icon_tooltip';
      chart.name = 'sdd_data_usage_vpn_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.cardStyle = ['card-2x1'];
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'sdd_data_usage_vpn_time_ts_metric_label',
      };
      chart.tag = {
        name: 'sdd_data_usage_vpn_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'sdd_data_usage_vpn_time_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.formatTime = true;
      chart.integerOnly = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });
  }
  page.applicationType = ['datausage'];
  page.icon = 'insight-icon-datausage';
  page.name = 'Device_Filter_Data_Usage';

  page.predefinedCharts = [
    {
      category: {
        name: 'Battery_Status_Charts_Category_Header_Time_Series',
        icon: 'insight-icon-linechart',
        tooltip: 'Battery_Status_Charts_Category_Header_Time_Series_Tooltip',
      },
      charts: [
        buildChartOptions(page.charts, 'ddTotalUsageTS_w', 'sdd_data_predefined_chart_total_usage_ts'),
        buildChartOptions(
          page.charts,
          'device-data-totalUsageCard_w',
          'sdd_data_predefined_chart_data_usage_by_conn_type'
        ),
        buildChartOptions(page.charts, 'ddAvgConnTimeTS_w', 'sdd_data_predefined_chart_avg_connection_time_ts'),
        buildChartOptions(page.charts, 'ddRoamUsageTS_w', 'sdd_data_predefined_chart_roam_usage_ts'),
        buildChartOptions(page.charts, 'ddRoamTimeTS_w', 'sdd_data_predefined_chart_roam_time_ts'),
        buildChartOptions(page.charts, 'ddVpnTimeTS_w', 'sdd_data_predefined_chart_vpn_time_ts'),
      ],
    },
    {
      category: {
        name: 'Data_Usage_Charts_Category_Header_Usage',
        icon: '',
        tooltip: 'Data_Usage_Charts_Category_Header_Usage_Tooltip',
      },
      charts: [
        buildChartOptions(page.charts, 'device-data-appUsageCard_w', 'Data_Usage_Charts_Category_Top_Apps_Data_Usage'),
        buildChartOptions(
          page.charts,
          'device-data-browserUsageCard_w',
          'Data_Usage_Charts_Category_Top_Browsers_Data_Usage'
        ),
        buildChartOptions(page.charts, 'device-data-uploadDownloadCard_w', 'Data_Usage_Charts_Category_Upload'),
      ],
    },
  ];
}

export function mapWindowsDeviceConfig(
  app: ApplicationConfigWithPages,
  otherApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  app.claims = [
    ProfilePermissionEnum.ProfileViewBatteryStatusDashboard,
    ProfilePermissionEnum.ProfileViewWifiDashboard,
    ProfilePermissionEnum.ProfileViewDataUsageDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    ProfilePermissionEnum.ProfileViewLastLocationDashboard,
    ProfilePermissionEnum.ProfileViewSignalStrengthDashboard,
    ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
    ProfilePermissionEnum.ProfileViewWebAppUsageDashboard,
  ];
  const pageIds = [findPageId(app.pages, DEVICE_LIST_W)];
  const childPageIds = [
    findPageId(app.pages, DEVICE_BATTERY_USAGE_W),
    findPageId(app.pages, DEVICE_DATA_USAGE_W),
    findPageId(otherApp.pages, DEVICE_APP_USAGE),
    findPageId(otherApp.pages, DEVICE_LAST_KNOWN),
    findPageId(app.pages, DEVICE_SIGNAL_STRENGTH_W),
    findPageId(otherApp.pages, DEVICE_WIFI),
    findPageId(otherApp.pages, SMART_BATTERY_SPOTLIGHT),
  ];
  app.name = 'Device_Filter_Device_List';

  patchPage(app, DEVICE_BATTERY_USAGE_W, patchDeviceWindowsBatteryPage);
  patchPage(app, DEVICE_DATA_USAGE_W, patchDeviceWindowsDataUsagePage);
  patchPage(app, DEVICE_LIST_W, patchWindowsDeviceListPage);
  patchPage(app, DEVICE_SIGNAL_STRENGTH_W, patchDeviceWindowsCellular);

  const defaultTabId = findPageId(app.pages, DEVICE_BATTERY_USAGE_W);

  app.pages.push(otherApp.pages.find(app => app?.baselineId === DEVICE_APP_USAGE));
  app.pages.push(otherApp.pages.find(app => app?.baselineId === DEVICE_LAST_KNOWN));
  app.pages.push(otherApp.pages.find(app => app?.baselineId === DEVICE_WIFI));
  app.pages.push(otherApp.pages.find(app => app?.baselineId === SMART_BATTERY_SPOTLIGHT));

  return { ...app, childPageIds, pageIds, defaultTabId };
}
